import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getUserDetails } from "modules/orderManagement/action";
import Loader from "components/loader";
import { Box, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getUserRoleLabel, USER_ROLE } from "utils/userType";
import { formateDateOfJoining } from "utils/constants";
import { formatCurrency } from "../../../utils/commonFunctions";
import UserDetailFieldCard from "./UserDetailFieldCard";
import DocumentPreviewModal from "components/documentPreviewModal";
import { DateRange } from "mui-daterange-picker";
import moment from "moment";
import OrderInformation from "./orderInformation";
import UserListing from "./userLising";
import { styles } from "./styles";

const initialsFromName = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) || "";
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase() || "U";
};

const UserDetail = () => {
  const dispatch: any = useDispatch();
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const userId = new URLSearchParams(window.location.search).get("userId");
  const [userListing, setUserListing] = useState<any>(null);
  const [startDate, setStartDate] = useState(moment().toDate());
  const [endDate, setEndDate] = useState(new Date());

  const dateRangeHandler = (range: DateRange) => {
    if (range.startDate) {
      setStartDate(range.startDate);
    }
    if (range.endDate) {
      setEndDate(range.endDate);
    }
  };

  const dateApplyHandler = () => {
    const start = moment(startDate).startOf("day").toISOString();
    const end = moment(endDate).endOf("day").toISOString();
    dispatch(
      getUserDetails(
        userId,
        (response: any) => {
          if (response?.isSuccess) {
            setUserDetails((prev: any) =>
              prev
                ? {
                    ...prev,
                    orderInformation: response.result?.orderInformation,
                  }
                : response.result,
            );
          }
        },
        { startDate: start, endDate: end },
      ),
    );
  };

  /**
   * USER DETAIL API function
   */
  const getUserDetail = () => {
    dispatch(
      getUserDetails(userId, (response: any) => {
        if (response?.isSuccess) {
          setUserDetails(response.result);
          setUserListing(response.result?.servicePartnerListing || []);
        }
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    setLoading(true);
    getUserDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return <Loader />;
  }

  if (!userDetails) {
    return (
      <Box sx={styles.stateCard}>
        <Typography sx={styles.stateTitle}>No user data found</Typography>
      </Box>
    );
  }

  const role = userDetails?.role || "";
  const isMerchant = role === USER_ROLE.MERCHANT;
  const isServicePartner = role === USER_ROLE.SERVICE_PARTNER;
  const isCoordinator = role === USER_ROLE.COORDINATOR;
  const documentUrl =
    userDetails?.document || userDetails?.documentUrl || "";
  const showDocument =
    (isServicePartner || isCoordinator) && Boolean(documentUrl);
  const addressParts = [
    userDetails?.addressLine1,
    userDetails?.addressLine2,
    userDetails?.city,
    userDetails?.state,
    userDetails?.pincode,
  ].filter(Boolean);
  const address =
    addressParts.length > 0 ? addressParts.join(", ") : "N/A";

  const documentLabel = (() => {
    try {
      const path = decodeURIComponent(`${documentUrl}`.split("?")[0] || "");
      const name = path.split("/").pop() || "";
      return name || "View document";
    } catch {
      return "View document";
    }
  })();

  return (
    <Box sx={styles.page}>
      <Box sx={styles.profile}>
        {userDetails?.profilePhoto?.length > 0 ? (
          <Box
            component="img"
            src={userDetails.profilePhoto}
            alt=""
            sx={styles.photo}
          />
        ) : (
          <Box sx={styles.initials} aria-hidden>
            {initialsFromName(userDetails?.fullName)}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={styles.name}>
            {userDetails?.fullName || "N/A"}
          </Typography>
          <Box component="span" sx={styles.role}>
            {getUserRoleLabel(role)}
          </Box>
          <Typography sx={styles.address}>
            <LocationOnOutlinedIcon />
            {address}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.sectionHead}>
          <Typography component="h2" sx={styles.sectionTitle}>
            Personal information
          </Typography>
        </Box>
        <Box sx={styles.grid}>
          <UserDetailFieldCard
            icon={<MailOutlineIcon />}
            heading="Email"
            data={userDetails?.email || "N/A"}
          />
          <UserDetailFieldCard
            icon={<PhoneOutlinedIcon />}
            heading="Phone number"
            data={
              userDetails?.phoneNumber
                ? `+91 ${userDetails.phoneNumber}`
                : "N/A"
            }
          />
          <UserDetailFieldCard
            icon={<EventOutlinedIcon />}
            heading="Date of joining"
            data={formateDateOfJoining(userDetails?.dateOfJoining)}
          />
          {isServicePartner && (
            <UserDetailFieldCard
              icon={<PaymentsOutlinedIcon />}
              heading="Salary"
              data={formatCurrency(Number(userDetails?.salary) || 0)}
            />
          )}
          {isMerchant && (
            <UserDetailFieldCard
              icon={<ReceiptLongOutlinedIcon />}
              heading="GST number"
              data={userDetails?.gstNumber || "N/A"}
            />
          )}
          {isMerchant && (
            <UserDetailFieldCard
              icon={<BadgeOutlinedIcon />}
              heading="Registration number"
              data={userDetails?.registrationNumber || "N/A"}
            />
          )}
          {isMerchant && (
            <UserDetailFieldCard
              icon={<PaymentsOutlinedIcon />}
              heading="Franchise amount"
              data={formatCurrency(Number(userDetails?.franchiseAmount) || 0)}
            />
          )}
          {isMerchant && (
            <UserDetailFieldCard
              icon={<GroupsOutlinedIcon />}
              heading="Service partners"
              data={`${userDetails?.numberOfStaff ?? 0}`}
            />
          )}
          {showDocument && (
            <UserDetailFieldCard
              icon={<DescriptionOutlinedIcon />}
              heading="Document"
              data={documentLabel}
              onClick={() => setDocPreviewOpen(true)}
            />
          )}
          {(isServicePartner || isCoordinator) && !documentUrl && (
            <UserDetailFieldCard
              icon={<DescriptionOutlinedIcon />}
              heading="Document"
              data="N/A"
            />
          )}
        </Box>
      </Box>
      {isMerchant && (
        <OrderInformation
          dashboardData={userDetails?.orderInformation}
          dateRangeHandler={dateRangeHandler}
          dateApplyHandler={dateApplyHandler}
          endDate={endDate}
          startDate={startDate}
          franchiseId={userId || userDetails?.id}
          franchiseName={userDetails?.fullName}
        />
      )}
      {isMerchant && <UserListing userList={userListing} />}
      <DocumentPreviewModal
        open={docPreviewOpen}
        onClose={() => setDocPreviewOpen(false)}
        url={documentUrl}
        title="Document preview"
      />
    </Box>
  );
};

export default UserDetail;
